// calcRoomsSummary
const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
};

const sumQty = (arr) => (Array.isArray(arr) ? arr : []).reduce((a, x) => a + Math.max(0, num(x?.qty ?? 1)), 0);

const sumOpeningsM2 = (arr) =>
    (Array.isArray(arr) ? arr : []).reduce((a, o) => {
        const qty = Math.max(0, num(o?.qty ?? 1));
        const w = Math.max(0, num(o?.widthMm));
        const h = Math.max(0, num(o?.heightMm));
        return a + (qty * w * h) / 1e6;
    }, 0);

const sumPointsQty = (arr) => (Array.isArray(arr) ? arr : []).reduce((a, p) => a + Math.max(0, num(p?.qty ?? 1)), 0);

const bySectionAdd = (map, arr) => {
    (Array.isArray(arr) ? arr : []).forEach((p) => {
        const sec = String(p?.cableSectionMm2 ?? "");
        const qty = Math.max(0, num(p?.qty ?? 1));
        if (!sec) return;
        map[sec] = (map[sec] || 0) + qty;
    });
};

const mergeMap = (a, b) => {
    const out = { ...(a || {}) };
    Object.keys(b || {}).forEach((k) => {
        out[k] = (out[k] || 0) + (b[k] || 0);
    });
    return out;
};

const addUniqueDoorsQty = (map, doorsArr) => {
    (Array.isArray(doorsArr) ? doorsArr : []).forEach((d) => {
        const qty = Math.max(0, num(d?.qty ?? 1));
        const keyRaw = d?.doorGroupId || d?.linkId || d?.sharedKey || d?.id || "";
        const key = String(keyRaw || "").trim();
        if (!key) return;
        const prev = map.get(key) || 0;
        if (qty > prev) map.set(key, qty);
    });
};

const ceilingLightsAdd = (map, arr) => {
    (Array.isArray(arr) ? arr : []).forEach((x) => {
        const t = String(x?.type || "").trim();
        const qty = Math.max(0, num(x?.qty ?? 1));
        if (!t) return;
        map[t] = (map[t] || 0) + qty;
    });
};

export const calcRoomsSummary = (rooms) => {
    const R = Array.isArray(rooms) ? rooms : [];

    let totalFloorM2 = 0;
    let totalCeilingM2 = 0;

    let totalWallsGrossM2 = 0;
    let totalOpeningsM2 = 0;

    let totalWindowsQty = 0;
    let totalSocketsQty = 0;
    let totalSwitchesQty = 0;

    let totalCeilingLightsQty = 0;
    let totalCeilingLightsByType = {};

    let totalSocketsBySection = {};
    let totalSwitchesBySection = {};

    const uniqueDoors = new Map();

    const roomSummaries = R.map((room) => {
        const shape = room?.shape === "polygon" ? "polygon" : "rectangle";
        const roomHeightMm = num(room?.heightMm ?? 2700);

        let floorM2 = 0;

        if (shape === "rectangle") {
            const L = Math.max(0, num(room?.rect?.lengthMm));
            const W = Math.max(0, num(room?.rect?.widthMm));
            const byRect = (L * W) / 1e6;

            if (byRect > 0) {
                floorM2 = byRect;
            } else {
                const bySavedArea = num(room?.area);
                floorM2 = bySavedArea > 0 ? bySavedArea : 0;
            }
        } else {
            const ov = num(room?.floorAreaM2Override);
            if (ov > 0) {
                floorM2 = ov;
            } else {
                const bySavedArea = num(room?.area);
                floorM2 = bySavedArea > 0 ? bySavedArea : 0;
            }
        }

        const ceilingOv = num(room?.ceilingAreaM2Override);
        const ceilingM2 = ceilingOv > 0 ? ceilingOv : floorM2;

        const ceilingLights = Array.isArray(room?.ceilingLights) ? room.ceilingLights : [];
        let ceilingLightsQty = 0;
        const ceilingLightsByType = {};
        ceilingLights.forEach((x) => {
            const q = Math.max(0, num(x?.qty ?? 1));
            ceilingLightsQty += q;
        });
        ceilingLightsAdd(ceilingLightsByType, ceilingLights);

        const walls = Array.isArray(room?.walls) ? room.walls : [];

        let wallsGrossM2 = 0;
        let openingsM2 = 0;
        let windowsQty = 0;
        let socketsQty = 0;
        let switchesQty = 0;

        const roomUniqueDoors = new Map();

        const socketsBySection = {};
        const switchesBySection = {};

        const wallSummaries = walls.map((w) => {
            const wallH = Math.max(0, num(w?.heightMmOverride) || roomHeightMm);
            const wallL = Math.max(0, num(w?.lengthMm));

            const grossM2 = (wallL * wallH) / 1e6;

            const doorsArr = Array.isArray(w?.doors) ? w.doors : [];
            const windowsArr = Array.isArray(w?.windows) ? w.windows : [];

            addUniqueDoorsQty(uniqueDoors, doorsArr);
            addUniqueDoorsQty(roomUniqueDoors, doorsArr);

            const doorM2 = sumOpeningsM2(doorsArr);
            const winM2 = sumOpeningsM2(windowsArr);

            const openM2 = doorM2 + winM2;
            const netM2 = Math.max(0, grossM2 - openM2);

            const dQtySides = sumQty(doorsArr);
            const wQty = sumQty(windowsArr);

            const socketsArr = Array.isArray(w?.sockets) ? w.sockets : [];
            const switchesArr = Array.isArray(w?.switches) ? w.switches : [];

            const sQty = sumPointsQty(socketsArr);
            const swQty = sumPointsQty(switchesArr);

            bySectionAdd(socketsBySection, socketsArr);
            bySectionAdd(switchesBySection, switchesArr);

            wallsGrossM2 += grossM2;
            openingsM2 += openM2;

            windowsQty += wQty;
            socketsQty += sQty;
            switchesQty += swQty;

            return {
                grossM2,
                openingsM2: openM2,
                netM2,
                doorsQty: dQtySides,
                windowsQty: wQty,
                socketsQty: sQty,
                switchesQty: swQty,
            };
        });

        let doorsQty = 0;
        roomUniqueDoors.forEach((q) => {
            doorsQty += Math.max(0, num(q));
        });

        const wallsNetM2 = Math.max(0, wallsGrossM2 - openingsM2);

        totalFloorM2 += floorM2;
        totalCeilingM2 += ceilingM2;

        totalWallsGrossM2 += wallsGrossM2;
        totalOpeningsM2 += openingsM2;

        totalWindowsQty += windowsQty;
        totalSocketsQty += socketsQty;
        totalSwitchesQty += switchesQty;

        totalCeilingLightsQty += ceilingLightsQty;
        totalCeilingLightsByType = mergeMap(totalCeilingLightsByType, ceilingLightsByType);

        totalSocketsBySection = mergeMap(totalSocketsBySection, socketsBySection);
        totalSwitchesBySection = mergeMap(totalSwitchesBySection, switchesBySection);

        return {
            floorM2,
            ceilingM2,
            roomTotals: {
                ceilingM2,
                ceilingLightsQty,
                ceilingLightsByType,
                wallsGrossM2,
                openingsM2,
                wallsNetM2,
                doorsQty,
                windowsQty,
                socketsQty,
                switchesQty,
                socketsBySection,
                switchesBySection,
            },
            walls: wallSummaries,
        };
    });

    let totalDoorsQty = 0;
    uniqueDoors.forEach((q) => {
        totalDoorsQty += Math.max(0, num(q));
    });

    const totals = {
        floorM2: totalFloorM2,
        ceilingM2: totalCeilingM2,
        ceilingLightsQty: totalCeilingLightsQty,
        ceilingLightsByType: totalCeilingLightsByType,
        wallsGrossM2: totalWallsGrossM2,
        openingsM2: totalOpeningsM2,
        wallsNetM2: Math.max(0, totalWallsGrossM2 - totalOpeningsM2),
        doorsQty: totalDoorsQty,
        windowsQty: totalWindowsQty,
        socketsQty: totalSocketsQty,
        switchesQty: totalSwitchesQty,
        socketsBySection: totalSocketsBySection,
        switchesBySection: totalSwitchesBySection,
    };

    return { rooms: roomSummaries, totals };
};
